<template>
  <KidHanziCardLayout :hide-role-image="face.style.hideRoleImage">
    <template #content>
      <DebutCardBadge
        v-if="face.isFirstLearn"
        class="absolute top-0 left-0"
      />

      <WrongCardBadge
        v-if="face.isWrongCard"
        class="absolute top-0 left-0"
      />

      <div
        class="w-full h-full flex justify-center items-center cursor-pointer"
        @click="onPlay"
      >
        <LottieBox
          ref="waveLottie"
          name="wave"
          class="h-full my--10"
          :autoplay="false"
          loop
        />
      </div>
    </template>

    <template #options>
      <div class="operation-grid">
        <Button
          v-for="(item, idx) of options"
          :key="idx"
          :class="{
            'aspect-square': true,
            wrong: data.selected === idx && !item.isAnswer,
          }"
          :scene="getOptionScene(item, idx)"
          option
          @click="onSelect(idx)"
        >
          <SparkleText
            class="sparkle-text"
            :tag="item.isAnswer && data.passed"
          >
            <div class="text-center text-96px">
              {{ item.hanzi }}
            </div>
          </SparkleText>
        </Button>
      </div>
    </template>
  </KidHanziCardLayout>
</template>

<script lang="ts" setup>
import { type KidHanziCardFace, UnitEventType } from '@/types/core'
import KidHanziCardLayout from '../../layout/KidHanziCardLayout.vue'
import { inject, onMounted, reactive, ref } from 'vue'
import { useHotKey } from '@/hooks'
import { FeedbackStar } from '../../common/feedback'
import { wait } from '@/utils/misc'
import DebutCardBadge from '@/components/ConcreteCard/common/DebutCardBadge.vue'
import WrongCardBadge from '../../common/WrongCardBadge.vue'
import LottieBox from '@/components/LottieBox.vue'
import SparkleText from '@/components/SparkleText.vue'
import { shuffle } from 'lodash-es'

const { face } = defineProps<{
  face: KidHanziCardFace
}>()

const data = reactive({
  star: FeedbackStar.Three,
  passed: false,
  selected: null as number | null,
})

type Option = {
  isAnswer: boolean
  hanzi: string
}

const options: Option[] = shuffle([
  { isAnswer: true, hanzi: face.card.hanzi[0][1] },
  { isAnswer: false, hanzi: shuffle(face.altCards)[0].hanzi[0][1] },
])

useHotKey('1', (evt: KeyboardEvent) => {
  evt.preventDefault()
  onSelect(0)
})

useHotKey('2', (evt: KeyboardEvent) => {
  evt.preventDefault()
  onSelect(1)
})

const waveLottie = ref<any>()

function onPlay() {
  waveLottie.value?.play()
  _am.playPinyin(face.card.hanzi[0][2]).then(() => {
    waveLottie.value?.pause()
  })
}

onMounted(async () => {
  // 预加载发音资源
  await _am.preload([{ type: 'pinyin', pinyin: face.card.hanzi[0][2] }])
  onPlay()
})

function getOptionScene(
  option: Option,
  idx: number
):
  | 'choice'
  | 'choiceSelected'
  | 'choiceCorrect'
  | 'choiceWrong'
  | 'choiceUsed' {
  if (data.selected === idx) {
    if (option.isAnswer) {
      return 'choiceCorrect'
    } else {
      return 'choiceWrong'
    }
  }
  return 'choice'
}

const onEvent = inject<(event: UnitEventType) => void>('onEvent')
const onStar = inject<(star: FeedbackStar) => void>('onStar')
const onNext = inject<VoidFunction>('onNext')

// 点击选项
function onSelect(idx: number) {
  if (data.passed) return

  const option = options[idx]
  data.selected = idx

  if (!option.isAnswer) {
    setTimeout(() => {
      data.selected = null
    }, 400)

    onEvent?.(UnitEventType.WRONG)
  } else {
    data.passed = true
    onCorrect()
  }

  if (data.passed) {
    onStar?.(data.star)
    autoNext()
  }
}

async function autoNext() {
  // TODO(buding): 如何准确获取动画结束，这里先 hardcode
  await wait(700)

  // 如果是新知识且设置的展示卡片详情，此时会先弹出卡片详情
  if (face.isFirstLearn && face.style.showCardDetail) {
    // showCardDetail(props.face.card, props.face.isFirstLearn, onNext)
    // return
  }

  onNext?.()
}

function onCorrect() {
  data.star = FeedbackStar.Three
  onEvent?.(UnitEventType.CORRECT)
}
</script>

<style scoped></style>
